import { tt } from '@agos/shared'

/**
 * The homepage, assembled from what the restaurant configured.
 *
 * `GET /api/v1/homepage-modules` needs only the tenant header and answers 12 rows with `moduleKey`,
 * `enabled`, `sortOrder` and a `content` blob. Measured, in order:
 *
 * ```
 * 0  promo_banner            off      6  chef_recommendations   on
 * 1  banner                  on       7  guarantees             on
 * 2  social_links            on       8  virtual_tour           on
 * 3  personalized_greeting   on       9  about_us               on
 * 4  most_ordered            on      10  newsletter             on
 * 5  popular_dishes          on      11  testimonials           off
 * ```
 *
 * Legacy ships **12** renderer components and the server configures **12** keys, one for one. (The
 * inventory's thirteenth entry is `index.ts`, the barrel.)
 *
 * **They match today, which is exactly why this says what happens when they stop.** A `moduleKey` with
 * no component is rendered as a stated gap, not dropped — that is FB-166's lesson, where the campaigns
 * catalogue held fifteen types, the screen drew thirteen, and the two missing ones were invisible
 * *precisely because nothing said a key had gone unmatched*. A homepage silently missing a section
 * looks like a homepage that was designed that way.
 */

export interface HomeModule {
  key: string
  enabled: boolean
  sortOrder: number
  content: Record<string, unknown>
}

/** The keys this build can draw. A key not in here is reported, never skipped. */
export const KNOWN_MODULES = [
  'promo_banner',
  'banner',
  'social_links',
  'personalized_greeting',
  'most_ordered',
  'popular_dishes',
  'chef_recommendations',
  'guarantees',
  'virtual_tour',
  'about_us',
  'newsletter',
  'testimonials',
] as const

export function modulesOf(payload: unknown): HomeModule[] {
  if (!Array.isArray(payload)) return []
  return payload
    .flatMap((entry) => {
      const row = entry as Record<string, unknown>
      const key = typeof row.moduleKey === 'string' ? row.moduleKey : ''
      if (key === '') return []
      return [
        {
          key,
          enabled: row.enabled === true,
          sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : 0,
          content:
            typeof row.content === 'object' && row.content !== null
              ? (row.content as Record<string, unknown>)
              : {},
        },
      ]
    })
    .sort((left, right) => left.sortOrder - right.sortOrder)
}

/** Switched on and drawable — the ordinary case. */
export function renderable(modules: readonly HomeModule[]): HomeModule[] {
  return modules.filter(
    (module) => module.enabled && (KNOWN_MODULES as readonly string[]).includes(module.key),
  )
}

/**
 * Switched on and **not** drawable.
 *
 * The restaurant configured a section, paid attention to it, and this build cannot render it. Silence
 * would make that look deliberate.
 */
export function unrenderable(modules: readonly HomeModule[]): HomeModule[] {
  return modules.filter(
    (module) => module.enabled && !(KNOWN_MODULES as readonly string[]).includes(module.key),
  )
}

export function unknownNote(modules: readonly HomeModule[]): string {
  const missing = unrenderable(modules)
  if (missing.length === 0) return ''
  return `${tt('Táto verzia stránky nevie zobraziť sekciu')}: ${missing.map((module) => module.key).join(', ')}.`
}


/** A string out of the module's content blob, which is free-form JSON per key. */
export function contentText(module: HomeModule, field: string): string | null {
  const value = module.content[field]
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

export function contentList(module: HomeModule, field: string): Record<string, unknown>[] {
  const value = module.content[field]
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : []
}

/**
 * Which SaaS module each homepage section belongs to.
 *
 * A franchise's package decides which of these it may show — the same list `/central/branding` answers
 * as `modules`. Without this gate a Basic franchise renders the sections its package does not include,
 * which is what AGOS did until the 2026-07-31 parity pass measured it: four sections on the home page
 * that legacy does not draw for this tenant.
 *
 * **`social_links` is deliberately absent**, and legacy says why: it is a basic feature on every
 * package, so mapping it would hide it on a Basic franchise whose package does not enumerate it.
 */
const MODULE_TIER_KEYS: Record<string, string> = {
  banner: 'customer.banner',
  promo_banner: 'customer.promo_banner',
  special_offer: 'customer.special_offer',
  personalized_greeting: 'customer.greeting',
  most_ordered: 'customer.frequent_orders',
  popular_dishes: 'customer.popular_dishes',
  chef_recommendations: 'customer.chef_recommendations',
  guarantees: 'customer.guarantees',
  about_us: 'customer.about_us',
  newsletter: 'customer.newsletter',
  virtual_tour: 'customer.virtual_tour',
  testimonials: 'customer.testimonials',
}

/**
 * The sections this franchise's package includes.
 *
 * **Fails open on purpose.** An empty or missing module list means the answer has not arrived — branding
 * is a separate request — and hiding the whole homepage while it is in flight is worse than showing a
 * section a package does not include. Legacy takes the same view.
 */
export function withinTier(modules: readonly HomeModule[], franchiseModules: readonly string[] | null): HomeModule[] {
  if (franchiseModules === null || franchiseModules.length === 0) return [...modules]
  return modules.filter((module) => {
    const tierKey = MODULE_TIER_KEYS[module.key]
    return tierKey === undefined || franchiseModules.includes(tierKey)
  })
}

/** The franchise's module list out of the branding payload, or `null` when it did not answer one. */
export function franchiseModulesOf(payload: unknown): string[] | null {
  if (typeof payload !== 'object' || payload === null) return null
  const raw = (payload as { modules?: unknown }).modules
  if (!Array.isArray(raw)) return null
  return raw.filter((entry): entry is string => typeof entry === 'string')
}
